import { API, BACKEND_URL } from '@/config/api';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/App';
import axios from 'axios';
import AdminNavigation from '@/components/AdminNavigation';
import MLPredictionPanel from '@/components/MLPredictionPanel';
import SatelliteFTPDownload from '@/components/SatelliteFTPDownload';
import { 
  Satellite, RefreshCw, MapPin, AlertTriangle, CheckCircle, 
  TrendingUp, TrendingDown, Minus, Info, Clock, Download,
  Loader2, ChevronDown, ChevronUp, Activity, Bell, CloudOff,
  AlertCircle, FileText, ExternalLink, Brain, HardDrive
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { APIProvider, Map as GoogleMap, AdvancedMarker, InfoWindow } from '@vis.gl/react-google-maps';
import { ChlorophyllHeatmap, RISK_CONFIG, NORWAY_CENTER } from '@/components/SatelliteMonitoringComponents';

export default function SatelliteMonitoring() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  
  // Data states
  const [productionAreas, setProductionAreas] = useState([]);
  const [thresholds, setThresholds] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  const [locationTimeSeries, setLocationTimeSeries] = useState(null);
  const [timeSeriesLoading, setTimeSeriesLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [systemStatus, setSystemStatus] = useState(null);
  const [dataTimestamp, setDataTimestamp] = useState(null);
  const [isCached, setIsCached] = useState(false);
  
  // Map states
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [mapCenter, setMapCenter] = useState(NORWAY_CENTER);
  const [mapZoom, setMapZoom] = useState(5);
  
  // Heatmap states
  const [heatmapData, setHeatmapData] = useState([]);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [heatmapVisible, setHeatmapVisible] = useState(true);
  const [showMarkers, setShowMarkers] = useState(true);
  const [heatmapStats, setHeatmapStats] = useState(null);
  
  // UI states
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedAreas, setExpandedAreas] = useState({});

  // Fetch heatmap data
  const fetchHeatmapData = useCallback(async (useDetailed = false) => {
    try {
      setHeatmapLoading(true);
      
      // Use detailed endpoint for actual satellite grid data
      const endpoint = useDetailed 
        ? `${API}/satellite/heatmap/detailed`
        : `${API}/satellite/heatmap`;
      
      const params = useDetailed
        ? {}  // Detailed endpoint fetches all areas by default
        : {
            min_lat: 57.5,
            max_lat: 71.5,
            min_lon: 3.0,
            max_lon: 31.0,
            resolution: 'medium'
          };
      
      const response = await axios.get(endpoint, {
        params,
        withCredentials: true,
        timeout: 180000  // 3 minute timeout for detailed data
      });
      
      if (response.data.success) {
        setHeatmapData(response.data.points || []);
        setHeatmapStats({
          ...response.data.stats,
          source: response.data.source,
          is_cached: response.data.is_cached
        });
      }
    } catch (err) {
      console.error('Error fetching heatmap data:', err);
      // Don't set error state, just leave heatmap empty
    } finally {
      setHeatmapLoading(false);
    }
  }, []);

  // Fetch thresholds
  const fetchThresholds = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/satellite/thresholds`, { withCredentials: true });
      setThresholds(response.data);
    } catch (err) {
      console.error('Error fetching thresholds:', err);
    }
  }, []);

  // Fetch production areas status
  const fetchProductionAreas = useCallback(async () => {
    try {
      setRefreshing(true);
      const response = await axios.get(`${API}/satellite/chlorophyll/production-areas`, { 
        withCredentials: true 
      });
      if (response.data.success) {
        setProductionAreas(response.data.areas);
        setDataTimestamp(response.data.timestamp);
        setIsCached(response.data.is_cached || false);
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching production areas:', err);
      if (err.response?.status === 504) {
        setError('Tidsavbrudd ved henting av satellittdata. Copernicus-tjenesten svarer sakte. Prøv igjen om litt.');
      } else {
        setError('Kunne ikke hente satellittdata. Prøv igjen senere.');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Fetch alerts
  const fetchAlerts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/satellite/alerts`, { withCredentials: true });
      if (response.data.success) {
        setAlerts(response.data.alerts || []);
      }
    } catch (err) {
      console.error('Error fetching alerts:', err);
    }
  }, []);

  // Fetch system status
  const fetchSystemStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/satellite/status`, { withCredentials: true });
      if (response.data.success) {
        setSystemStatus(response.data);
      }
    } catch (err) {
      console.error('Error fetching system status:', err);
    }
  }, []);

  // Fetch time series for a location
  const fetchLocationTimeSeries = useCallback(async (lat, lon, areaName) => {
    try {
      setTimeSeriesLoading(true);
      const response = await axios.get(`${API}/satellite/chlorophyll/location`, {
        params: { lat, lon, days: 30 },
        withCredentials: true
      });
      if (response.data.success) {
        setLocationTimeSeries({
          ...response.data,
          area_name: areaName
        });
      }
    } catch (err) {
      console.error('Error fetching time series:', err);
    } finally {
      setTimeSeriesLoading(false);
    }
  }, []);

  // Export CSV
  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/satellite/export/csv`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `satellittdata_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting CSV:', err);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchThresholds();
    fetchProductionAreas();
    fetchAlerts();
    fetchSystemStatus();
  }, [fetchThresholds, fetchProductionAreas, fetchAlerts, fetchSystemStatus]);

  // Fetch heatmap data when map tab is active
  useEffect(() => {
    if (activeTab === 'map' && heatmapData.length === 0 && !heatmapLoading) {
      fetchHeatmapData();
    }
  }, [activeTab, heatmapData.length, heatmapLoading, fetchHeatmapData]);

  // Handle area selection
  const handleAreaSelect = (area) => {
    setSelectedArea(area);
    setMapCenter({ lat: area.center.lat, lng: area.center.lon });
    setMapZoom(8);
    if (area.chlorophyll?.mean) {
      fetchLocationTimeSeries(area.center.lat, area.center.lon, area.name);
    } else {
      setLocationTimeSeries(null);
    }
  };

  // Toggle area expansion
  const toggleAreaExpansion = (areaId) => {
    setExpandedAreas(prev => ({
      ...prev,
      [areaId]: !prev[areaId]
    }));
  };

  // Calculate summary statistics
  const summaryStats = {
    totalAreas: productionAreas.length,
    highRisk: productionAreas.filter(a => ['high', 'very_high', 'extreme'].includes(a.risk_level)).length,
    moderateRisk: productionAreas.filter(a => a.risk_level === 'moderate').length,
    lowRisk: productionAreas.filter(a => a.risk_level === 'low').length,
    unknown: productionAreas.filter(a => a.risk_level === 'unknown').length
  };

  // Format chlorophyll value
  const formatChl = (value) => {
    if (value === null || value === undefined) return '-';
    return `${value.toFixed(2)} mg/m³`;
  };

  // Get trend icon
  const getTrendIcon = (trend) => {
    if (trend === 'increasing') return <TrendingUp className="w-4 h-4 text-red-500" />;
    if (trend === 'decreasing') return <TrendingDown className="w-4 h-4 text-green-500" />;
    return <Minus className="w-4 h-4 text-gray-500" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
        <AdminNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
            <p className="text-slate-600 font-medium">Henter satellittdata fra Copernicus...</p>
            <p className="text-slate-500 text-sm mt-2">Dette kan ta opptil 3 minutter første gang</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      <AdminNavigation />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-teal-500 rounded-xl">
                <Satellite className="w-6 h-6 text-white" />
              </div>
              Satellittmonitorering
            </h1>
            <p className="text-slate-600 mt-1">
              Klorofyllkonsentrasjon fra Copernicus Marine Service
            </p>
          </div>
          
          <div className="flex items-center gap-3 flex-wrap">
            {dataTimestamp && (
              <Badge variant="outline" className="bg-white">
                <Clock className="w-3 h-3 mr-1" />
                Data fra: {dataTimestamp}
                {isCached && <span className="ml-1 text-xs">(cached)</span>}
              </Badge>
            )}
            <Button 
              onClick={handleExportCSV}
              variant="outline"
              className="bg-white"
              size="sm"
            >
              <Download className="w-4 h-4 mr-2" />
              Eksporter
            </Button>
            <Button 
              onClick={() => {
                fetchProductionAreas();
                fetchAlerts();
                fetchSystemStatus();
              }}
              disabled={refreshing}
              variant="outline"
              className="bg-white"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Henter data...' : 'Oppdater'}
            </Button>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Feil</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Active Alerts Banner */}
        {alerts.length > 0 && (
          <Alert className="mb-6 border-orange-300 bg-orange-50">
            <Bell className="h-4 w-4 text-orange-600" />
            <AlertTitle className="text-orange-800">Aktive varsler ({alerts.length})</AlertTitle>
            <AlertDescription className="text-orange-700">
              {alerts.slice(0, 2).map((alert, i) => (
                <div key={i} className="mt-1">
                  <strong>{alert.area_name}:</strong> {alert.message}
                </div>
              ))}
              {alerts.length > 2 && (
                <Button variant="link" className="p-0 h-auto text-orange-700" onClick={() => setActiveTab('alerts')}>
                  Se alle varsler →
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Data Coverage Warning */}
        {summaryStats.unknown > summaryStats.totalAreas / 2 && (
          <Alert className="mb-6 border-blue-300 bg-blue-50">
            <CloudOff className="h-4 w-4 text-blue-600" />
            <AlertTitle className="text-blue-800">Begrenset datadekning</AlertTitle>
            <AlertDescription className="text-blue-700">
              {summaryStats.unknown} av {summaryStats.totalAreas} områder mangler satellittdata. 
              Dette er vanlig om vinteren pga. skydekke og begrenset sollys. 
              Data oppdateres når værforholdene tillater det.
            </AlertDescription>
          </Alert>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white/80 backdrop-blur">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Oversikt
            </TabsTrigger>
            <TabsTrigger value="map" className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Kart
            </TabsTrigger>
            <TabsTrigger value="alerts" className="flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Varsler
              {alerts.length > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center">
                  {alerts.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="ml" className="flex items-center gap-2">
              <Brain className="w-4 h-4" />
              ML Prediksjon
            </TabsTrigger>
            <TabsTrigger value="download" className="flex items-center gap-2">
              <HardDrive className="w-4 h-4" />
              FTP Nedlasting
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              <Card className="bg-white/80 backdrop-blur border-slate-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Områder</p>
                      <p className="text-2xl font-bold text-slate-800">{summaryStats.totalAreas}</p>
                    </div>
                    <div className="p-3 bg-blue-100 rounded-full">
                      <MapPin className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/80 backdrop-blur border-red-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Høy risiko</p>
                      <p className="text-2xl font-bold text-red-600">{summaryStats.highRisk}</p>
                    </div>
                    <div className="p-3 bg-red-100 rounded-full">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/80 backdrop-blur border-yellow-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Moderat</p>
                      <p className="text-2xl font-bold text-yellow-600">{summaryStats.moderateRisk}</p>
                    </div>
                    <div className="p-3 bg-yellow-100 rounded-full">
                      <Activity className="w-5 h-5 text-yellow-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/80 backdrop-blur border-green-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Lav risiko</p>
                      <p className="text-2xl font-bold text-green-600">{summaryStats.lowRisk}</p>
                    </div>
                    <div className="p-3 bg-green-100 rounded-full">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/80 backdrop-blur border-gray-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Ingen data</p>
                      <p className="text-2xl font-bold text-gray-600">{summaryStats.unknown}</p>
                    </div>
                    <div className="p-3 bg-gray-100 rounded-full">
                      <CloudOff className="w-5 h-5 text-gray-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Winter Coverage Warning */}
            {summaryStats.unknown > summaryStats.lowRisk + summaryStats.mediumRisk + summaryStats.highRisk && (
              <Card className="bg-amber-50/80 backdrop-blur border-amber-200">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-amber-100 rounded-full shrink-0">
                      <CloudOff className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-amber-800 mb-1">Begrenset satellittdekning</h4>
                      <p className="text-sm text-amber-700">
                        Det er for tiden begrenset satellittdata tilgjengelig for Norskekysten. 
                        Dette skyldes <strong>vinterforhold</strong> med kort daglengde, mørketid i nord, 
                        og hyppig skydekke som hindrer satellittmålinger av havoverflaten.
                      </p>
                      <p className="text-sm text-amber-600 mt-2">
                        Normal dekning forventes fra mars-april når dagslyset øker.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Threshold Legend */}
            {thresholds && (
              <Card className="bg-white/80 backdrop-blur">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Info className="w-5 h-5 text-slate-500" />
                    Klorofyll Risikonivåer
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-4">
                    {Object.entries(thresholds.risk_levels).map(([level, info]) => (
                      <TooltipProvider key={level}>
                        <Tooltip>
                          <TooltipTrigger>
                            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50">
                              <div 
                                className="w-4 h-4 rounded-full"
                                style={{ backgroundColor: info.color }}
                              />
                              <span className="text-sm font-medium text-slate-700">
                                {RISK_CONFIG[level]?.label || level}
                              </span>
                              {thresholds.thresholds[level] && (
                                <span className="text-xs text-slate-500">
                                  &lt; {thresholds.thresholds[level]} {thresholds.unit}
                                </span>
                              )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{info.description}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Production Areas List */}
            <Card className="bg-white/80 backdrop-blur">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-teal-600" />
                  Status per produksjonsområde
                </CardTitle>
                <CardDescription>
                  Klikk på et område for å se tidsseriedata
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {productionAreas.map((area) => {
                    const riskConfig = RISK_CONFIG[area.risk_level] || RISK_CONFIG.unknown;
                    const isExpanded = expandedAreas[area.area_id];
                    const isSelected = selectedArea?.area_id === area.area_id;
                    const hasData = area.chlorophyll?.mean !== null && area.chlorophyll?.mean !== undefined;
                    
                    return (
                      <div 
                        key={area.area_id}
                        className={`p-4 rounded-xl border transition-all cursor-pointer ${
                          isSelected 
                            ? `${riskConfig.bgLight} ${riskConfig.border} ring-2 ring-offset-1`
                            : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={() => handleAreaSelect(area)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                              style={{ backgroundColor: riskConfig.color }}
                            >
                              {area.area_id.replace('PO', '')}
                            </div>
                            <div>
                              <h4 className="font-semibold text-slate-800">{area.area_id}</h4>
                              <p className="text-sm text-slate-500">{area.name}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            {hasData ? (
                              <div className="text-right mr-2">
                                <p className="text-sm font-medium text-slate-700">
                                  {formatChl(area.chlorophyll?.mean)}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {area.data_date || 'ukjent dato'}
                                </p>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 text-slate-400 mr-2">
                                <CloudOff className="w-4 h-4" />
                                <span className="text-sm">Ingen data</span>
                              </div>
                            )}
                            <Badge className={`${riskConfig.bg} text-white`}>
                              {riskConfig.label}
                            </Badge>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleAreaExpansion(area.area_id);
                              }}
                              className="p-1 hover:bg-slate-200 rounded"
                            >
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4 text-slate-500" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-slate-500" />
                              )}
                            </button>
                          </div>
                        </div>
                        
                        {isExpanded && (
                          <div className="mt-4 pt-4 border-t border-slate-200">
                            {hasData ? (
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-xs text-slate-500 uppercase">Gjennomsnitt</p>
                                  <p className="text-lg font-semibold text-slate-800">
                                    {formatChl(area.chlorophyll?.mean)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-slate-500 uppercase">Maksimum</p>
                                  <p className="text-lg font-semibold text-slate-800">
                                    {formatChl(area.chlorophyll?.max)}
                                  </p>
                                </div>
                                <div className="col-span-2">
                                  <p className="text-xs text-slate-500 uppercase">Koordinater</p>
                                  <p className="text-sm text-slate-600">
                                    {area.center.lat.toFixed(2)}°N, {area.center.lon.toFixed(2)}°E
                                  </p>
                                </div>
                              </div>
                            ) : (
                              <div className="text-center py-4">
                                <CloudOff className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                                <p className="text-sm text-slate-500">
                                  Satellittdata er ikke tilgjengelig for dette området.
                                </p>
                                <p className="text-xs text-slate-400 mt-1">
                                  Årsak: Skydekke, land-mask eller mangel på sollys (vanlig om vinteren)
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Map Tab */}
          <TabsContent value="map" className="space-y-6">
            {/* Heatmap Controls */}
            <Card className="bg-white/80 backdrop-blur">
              <CardContent className="py-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={heatmapVisible}
                          onChange={(e) => setHeatmapVisible(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
                        <span className="ml-2 text-sm font-medium text-slate-700">Vis varmekart</span>
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showMarkers}
                          onChange={(e) => setShowMarkers(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
                        <span className="ml-2 text-sm font-medium text-slate-700">Vis områdemarkører</span>
                      </label>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {heatmapLoading && (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Henter varmekartdata...
                      </div>
                    )}
                    {heatmapStats && !heatmapLoading && (
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-slate-500">
                          {(heatmapStats.total_points || heatmapStats.valid_points || 0).toLocaleString()} datapunkter
                        </span>
                        {heatmapStats.coverage_percent && (
                          <span className="text-slate-500">
                            Dekning: {heatmapStats.coverage_percent}%
                          </span>
                        )}
                        {heatmapStats.source && (
                          <Badge variant={heatmapStats.source === 'satellite_grid' ? 'default' : 'secondary'}>
                            {heatmapStats.source === 'satellite_grid' ? 'Detaljert (4km grid)' : 'Områdebasert'}
                          </Badge>
                        )}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchHeatmapData(false)}
                        disabled={heatmapLoading}
                        title="Rask lasting med områdegjennomsnitt"
                      >
                        <RefreshCw className={`w-4 h-4 mr-1 ${heatmapLoading ? 'animate-spin' : ''}`} />
                        Rask
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => fetchHeatmapData(true)}
                        disabled={heatmapLoading}
                        title="Detaljert visning med faktisk satellittgrid (4km oppløsning)"
                        className="bg-teal-600 hover:bg-teal-700"
                      >
                        <Satellite className={`w-4 h-4 mr-1 ${heatmapLoading ? 'animate-spin' : ''}`} />
                        Detaljert
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Color Legend */}
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs text-slate-500 mb-2">Klorofyllkonsentrasjon (mg/m³)</p>
                  <div className="flex items-center gap-1">
                    <div className="h-4 flex-1 rounded-l" style={{ background: 'linear-gradient(to right, #1e3a5f, #2563eb, #22c55e, #84cc16, #eab308, #f97316, #ef4444, #7c2d12)' }}></div>
                    <div className="flex justify-between w-full text-xs text-slate-500 mt-1 absolute left-0 right-0 px-2" style={{ marginTop: '20px' }}>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    <span>0</span>
                    <span>1</span>
                    <span>2</span>
                    <span>5</span>
                    <span>10</span>
                    <span>20+</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Map */}
              <Card className="lg:col-span-2 bg-white/80 backdrop-blur overflow-hidden">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-teal-600" />
                    Klorofyllkart - Norskekysten
                  </CardTitle>
                  <CardDescription>
                    {heatmapVisible && heatmapData.length > 0 
                      ? 'Varmekart viser klorofyllkonsentrasjon i sanntid'
                      : 'Aktiver varmekart for å se klorofylldata'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="h-[550px]">
                    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}>
                      <GoogleMap
                        defaultCenter={NORWAY_CENTER}
                        center={selectedArea ? mapCenter : undefined}
                        defaultZoom={5}
                        zoom={selectedArea ? mapZoom : undefined}
                        mapId="satellite-monitoring-map"
                        gestureHandling="greedy"
                        disableDefaultUI={false}
                        mapTypeId="terrain"
                      >
                        {/* Heatmap Layer */}
                        <ChlorophyllHeatmap 
                          points={heatmapData} 
                          visible={heatmapVisible && heatmapData.length > 0} 
                        />
                        
                        {/* Production Area Markers */}
                        {showMarkers && productionAreas.map((area) => {
                          const riskConfig = RISK_CONFIG[area.risk_level] || RISK_CONFIG.unknown;
                          return (
                            <AdvancedMarker
                              key={area.area_id}
                              position={{ lat: area.center.lat, lng: area.center.lon }}
                              onClick={() => {
                                setSelectedMarker(area);
                                handleAreaSelect(area);
                              }}
                            >
                              <div 
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs shadow-lg cursor-pointer transform hover:scale-110 transition-transform"
                                style={{ backgroundColor: riskConfig.color }}
                              >
                                {area.area_id.replace('PO', '')}
                              </div>
                            </AdvancedMarker>
                          );
                        })}
                        
                        {selectedMarker && (
                          <InfoWindow
                            position={{ lat: selectedMarker.center.lat, lng: selectedMarker.center.lon }}
                            onCloseClick={() => setSelectedMarker(null)}
                          >
                            <div className="p-2 min-w-[200px]">
                              <h3 className="font-bold text-slate-800">{selectedMarker.area_id}</h3>
                              <p className="text-sm text-slate-600 mb-2">{selectedMarker.name}</p>
                              <div className="space-y-1">
                                <div className="flex justify-between text-sm">
                                  <span className="text-slate-500">Gjennomsnitt:</span>
                                  <span className="font-medium">{formatChl(selectedMarker.chlorophyll?.mean)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-slate-500">Maksimum:</span>
                                  <span className="font-medium">{formatChl(selectedMarker.chlorophyll?.max)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-slate-500">Risikonivå:</span>
                                  <Badge 
                                    className={`${RISK_CONFIG[selectedMarker.risk_level]?.bg || 'bg-gray-500'} text-white`}
                                  >
                                    {RISK_CONFIG[selectedMarker.risk_level]?.label || 'Ukjent'}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          </InfoWindow>
                        )}
                      </GoogleMap>
                    </APIProvider>
                  </div>
                </CardContent>
              </Card>

              {/* Time Series */}
              <Card className="bg-white/80 backdrop-blur">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-teal-600" />
                    Tidsutvikling
                  </CardTitle>
                  <CardDescription>
                    {selectedArea ? selectedArea.name : 'Velg et område fra kartet'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {timeSeriesLoading ? (
                    <div className="h-64 flex items-center justify-center">
                      <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                    </div>
                  ) : locationTimeSeries?.time_series?.length > 0 ? (
                    <div className="space-y-4">
                      {/* Mini stats */}
                      <div className="grid grid-cols-2 gap-2">
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-xs text-slate-500">Nåværende</p>
                          <p className="text-lg font-bold text-slate-800">
                            {locationTimeSeries.analysis.current_value?.toFixed(2) || '-'}
                          </p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-xs text-slate-500">Trend</p>
                          <div className="flex items-center gap-1">
                            {getTrendIcon(locationTimeSeries.analysis.trend)}
                            <span className="text-lg font-bold text-slate-800">
                              {locationTimeSeries.analysis.change_percent > 0 ? '+' : ''}
                              {locationTimeSeries.analysis.change_percent?.toFixed(0) || 0}%
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Chart */}
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={locationTimeSeries.time_series.map(d => ({
                            ...d,
                            date: new Date(d.timestamp).toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit' })
                          }))}>
                            <defs>
                              <linearGradient id="colorChl" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.3}/>
                                <stop offset="95%" stopColor="#14b8a6" stopOpacity={0}/>
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <RechartsTooltip 
                              formatter={(value) => [`${value.toFixed(2)} mg/m³`, 'Klorofyll']}
                            />
                            <Area 
                              type="monotone" 
                              dataKey="value" 
                              stroke="#14b8a6" 
                              fill="url(#colorChl)"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  ) : selectedArea && !selectedArea.chlorophyll?.mean ? (
                    <div className="h-64 flex items-center justify-center">
                      <div className="text-center">
                        <CloudOff className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p className="text-slate-500">Ingen tidsseriedata tilgjengelig</p>
                        <p className="text-sm text-slate-400 mt-1">Dette området mangler satellittdata</p>
                      </div>
                    </div>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-slate-500">
                      <div className="text-center">
                        <MapPin className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p>Velg et produksjonsområde</p>
                        <p className="text-sm mt-1">Klikk på et område i kartet</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts" className="space-y-6">
            <Card className="bg-white/80 backdrop-blur">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-orange-600" />
                  Varsler og anbefalinger
                </CardTitle>
                <CardDescription>
                  Automatiske varsler basert på klorofyllnivåer
                </CardDescription>
              </CardHeader>
              <CardContent>
                {alerts.length > 0 ? (
                  <div className="space-y-4">
                    {alerts.map((alert, index) => {
                      const severityConfig = {
                        critical: { bg: 'bg-red-50', border: 'border-red-300', icon: AlertCircle, iconColor: 'text-red-600' },
                        alert: { bg: 'bg-orange-50', border: 'border-orange-300', icon: AlertTriangle, iconColor: 'text-orange-600' },
                        warning: { bg: 'bg-yellow-50', border: 'border-yellow-300', icon: Info, iconColor: 'text-yellow-600' }
                      };
                      const config = severityConfig[alert.severity] || severityConfig.warning;
                      const Icon = config.icon;
                      
                      return (
                        <div key={index} className={`p-4 rounded-xl border ${config.bg} ${config.border}`}>
                          <div className="flex items-start gap-3">
                            <Icon className={`w-5 h-5 mt-0.5 ${config.iconColor}`} />
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <h4 className="font-semibold text-slate-800">
                                  {alert.area_id} - {alert.area_name}
                                </h4>
                                <Badge className={RISK_CONFIG[alert.risk_level]?.bg + ' text-white'}>
                                  {RISK_CONFIG[alert.risk_level]?.label}
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-700 mb-2">{alert.message}</p>
                              <div className="p-3 bg-white/50 rounded-lg">
                                <p className="text-xs text-slate-500 uppercase mb-1">Anbefaling</p>
                                <p className="text-sm text-slate-700">{alert.recommendation}</p>
                              </div>
                              {alert.data_date && (
                                <p className="text-xs text-slate-500 mt-2">
                                  Data fra: {alert.data_date}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Ingen aktive varsler</h3>
                    <p className="text-slate-500">
                      Alle overvåkede områder har normale klorofyllnivåer
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ML Prediction Tab */}
          <TabsContent value="ml" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <MLPredictionPanel 
                selectedAreaId={selectedArea?.area_id}
              />
              
              {/* ML Info Card */}
              <Card className="bg-white/80 backdrop-blur">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Brain className="w-5 h-5 text-purple-600" />
                    Om ML-prediksjoner
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <h4 className="font-medium text-purple-800 mb-2">Hvordan det fungerer</h4>
                    <p className="text-sm text-purple-700">
                      ML-modellen analyserer historiske klorofylldata og bruker lineær regresjon 
                      for å identifisere trender og forutsi fremtidige nivåer. Prediksjoner tar 
                      hensyn til sesongvariasjoner og nåværende trender.
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    <h4 className="font-medium text-slate-700">Risikofaktorer som vurderes:</h4>
                    <ul className="space-y-2 text-sm text-slate-600">
                      <li className="flex items-start gap-2">
                        <Activity className="w-4 h-4 text-blue-500 mt-0.5" />
                        <span><strong>Nåværende nivå:</strong> Gjeldende klorofyllkonsentrasjon</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <TrendingUp className="w-4 h-4 text-orange-500 mt-0.5" />
                        <span><strong>Trend:</strong> Om nivåene øker eller synker</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Clock className="w-4 h-4 text-green-500 mt-0.5" />
                        <span><strong>Sesong:</strong> Vår/sommer har høyere risiko</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <h4 className="font-medium text-amber-800 mb-2">Viktig å vite</h4>
                    <p className="text-sm text-amber-700">
                      ML-prediksjoner er veiledende og bør brukes sammen med andre datakilder 
                      og faglig vurdering. Nøyaktigheten avhenger av datatilgjengelighet og 
                      hvor representative historiske mønstre er for fremtiden.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* FTP Download Tab */}
          <TabsContent value="download" className="space-y-6">
            <SatelliteFTPDownload />
          </TabsContent>
        </Tabs>

        {/* Data Source Info */}
        <Card className="mt-8 bg-gradient-to-r from-blue-50 to-teal-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-full">
                <Satellite className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800 mb-1">Om datakilden</h3>
                <p className="text-sm text-slate-600 mb-3">
                  Klorofylldata hentes fra <strong>Copernicus Marine Service</strong> - EUs havovervåkningsprogram. 
                  Datasettet bruker satellittobservasjoner for å beregne klorofyll-a konsentrasjon i havoverflaten.
                  Om vinteren kan datadekningen være begrenset pga. skydekke og mangel på sollys.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="bg-white">
                    Datasett: NRT L4 Gap-free Multi 4km
                  </Badge>
                  <Badge variant="outline" className="bg-white">
                    Oppløsning: 4 km
                  </Badge>
                  <Badge variant="outline" className="bg-white">
                    Oppdatering: Daglig
                  </Badge>
                  <a 
                    href="https://marine.copernicus.eu/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                  >
                    <ExternalLink className="w-3 h-3" />
                    marine.copernicus.eu
                  </a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
